import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    TemplateRef,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    contentChild,
    input,
    model,
    output,
    signal,
    viewChild,
} from '@angular/core';

export const DS_ACCORDION_SIZE_ARRAY = ['medium', 'large'] as const;
export type DsAccordionSize = (typeof DS_ACCORDION_SIZE_ARRAY)[number];

export const DS_ACCORDION_VARIANT_ARRAY = ['transparent', 'surface-lowest', 'surface-low', 'surface-high', 'surface-highest'] as const;
export type DsAccordionVariant = (typeof DS_ACCORDION_VARIANT_ARRAY)[number];

@Component({
    selector: 'ds-accordion',
    template: ` <div class="ds-accordion-header-wrapper">
            <ng-content select="ds-accordion-header" />
        </div>
        <div class="ds-accordion-content" #accordionContent [class.ds-accordion-content-closed]="!isOpen()()">
            @if (isOpen()()) {
                @if (contentTemplate()) {
                    <ng-container [ngTemplateOutlet]="contentTemplate() ?? null" />
                } @else {
                    <ng-content select="ds-accordion-content" />
                }
            }
        </div>`,
    host: {
        '[class]': 'hostClass()',
        '[class.ds-accordion-card]': 'withCard()',
        '[class.ds-accordion-inverse]': 'inverse()',
        '[class.ds-accordion-opened]': 'isOpen()()',
        '[style.--ds-accordion-content-height]': 'contentHeight()',
        '[class.ds-accordion-animate]': 'animate()',
    },
    styleUrl: 'accordion.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet],
})
export class DsAccordion implements OnDestroy {
    // Accessibility
    public readonly openAccordionLabel = input('Open Accordion');
    public readonly closeAccordionLabel = input('Close Accordion');

    public readonly variant = model<DsAccordionVariant>('transparent');
    public readonly size = model<DsAccordionSize>('large'); // This property is related to the accordion-header
    public readonly inverse = model(false);

    // TODO: use with linkedSignal when monorepo uses v19, and drop custom impl
    public readonly open = input(false, { transform: booleanAttribute });
    public readonly withCard = input(false, { transform: booleanAttribute });
    public readonly openedChange = output<boolean>();

    protected readonly contentTemplate = contentChild('content', { read: TemplateRef });

    // TODO: convert to linkedSignal, -> writable computed pattern here, get the value using isOpen()()
    public readonly isOpen = computed(() => signal(this.open()));

    protected readonly hostClass = computed(() => `ds-accordion ds-accordion-${this.variant()} ds-accordion-${this.size()}`);
    protected readonly accordionContent = viewChild('accordionContent', { read: ElementRef });
    protected resizeObserver: ResizeObserver = new ResizeObserver(() => this.updateHeight());
    protected readonly contentHeight = signal('0px');
    public readonly animate = input(false);

    toggle() {
        this.isOpen().update((x) => !x);
        this.openedChange.emit(this.isOpen()());
        this.updateHeight();
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver.unobserve(this.accordionContent()?.nativeElement as HTMLElement);
        }
    }
    ngAfterViewInit() {
        this.resizeObserver.observe(this.accordionContent()?.nativeElement);
    }

    private updateHeight() {
        if (this.animate()) {
            const el = this.accordionContent()?.nativeElement;
            const open = this.isOpen()();
            if (open) {
                requestAnimationFrame(() => {
                    this.contentHeight.set(`${el.scrollHeight}px`);
                });
            } else {
                this.contentHeight.set('0');
            }
        }
    }
}
